// @flow

import * as React from 'react';
import {useEffect, useState} from 'react';

import {loadStripe} from '@stripe/stripe-js';
import {Elements, useElements} from '@stripe/react-stripe-js';

import Box from 'sail/Box';
import Group from 'sail/Group';
import {Text, Title, Intro} from 'sail/Text';

import Card from 'sail/Card';

import DataTable, {
  DataTableBody,
  DataTableCell,
  DataTableHead,
  DataTableRow,
} from 'sail/DataTable';

import {CodeBlock} from 'docs/components';
import {useAccount} from 'docs/data';

import {getAffirmLogoLink} from './logo-images';

import './AffirmMessagingDemo.scss';

type AffirmMessagingProps = {|
  options?: any,
|};

const AffirmMessaging = ({options}: AffirmMessagingProps) => {
  const elements = useElements();
  const ref = React.useRef();

  useEffect(() => {
    if (!elements) {
      // Wait for Stripe.js to load
      return;
    }

    const affirmMessage = elements.create('affirmMessage', {
      amount: 5000,
      currency: 'USD',
      ...options,
    });
    affirmMessage.mount(ref.current);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */ // TODO: use correct dependencies
  }, [elements]);

  return <div className="AffirmMessageWrapper" ref={ref} />;
};

const AffirmLogoImage = ({color}: {color: string}) => {
  return (
    <img
      width="100%"
      height="100%"
      style={{
        objectFit: 'contain',
        objectPosition: 'center',
      }}
      src={getAffirmLogoLink(color)}
      alt=""
    />
  );
};

const ProductDemo = () => {
  return (
    <Box
      className="DocsDemo"
      background="offset"
      margin={{vertical: 20}}
      padding={20}
      flex={{direction: 'row'}}
    >
      <Box padding={{right: 12}}>
        <div className="DocsDemo-PrinceOfPersiaImage" />
      </Box>
      <Group direction="vertical" spacing={8}>
        <Title className="title">
          The Making of Prince of Persia: Journals 1985-1993
        </Title>
        <Intro>Jordan Mechner</Intro>
        <Box background="white" flex={{direction: 'column'}} padding={12}>
          <Title>$50</Title>
          <AffirmMessaging />
        </Box>
      </Group>
    </Box>
  );
};

const LogoDemo = () => {
  return (
    <DataTable>
      <DataTableHead
        columns={[{label: 'primary'}, {label: 'black'}, {label: 'white'}]}
      />
      <DataTableBody>
        <DataTableRow>
          <DataTableCell align="center">
            <AffirmLogoImage color="primary" />
          </DataTableCell>
          <DataTableCell align="center">
            <AffirmLogoImage color="black" />
          </DataTableCell>
          <DataTableCell align="center" className="background-gray">
            <AffirmLogoImage color="white" />
          </DataTableCell>
        </DataTableRow>
      </DataTableBody>
    </DataTable>
  );
};

const CodeAndPreview = ({preview, codeBlock, label}) => {
  return (
    <Box margin={{top: 24, bottom: 24}}>
      <Box>
        <Card radius="top" shadow="keyline">
          <Box padding={{all: 20}}>{preview}</Box>
        </Card>
      </Box>
      <Box
        padding={{vertical: 4, horizontal: 12}}
        background="gray50"
        divider={{color: 'light', width: 1, side: 'all'}}
        style={{
          margin: '0 -1px',
        }}
      >
        <Text color="gray" weight="medium">
          {label}
        </Text>
      </Box>
      <Box className="CodeContainer">{codeBlock}</Box>
    </Box>
  );
};

const CSSDemoMessage = () => (
  <div>
    <CodeAndPreview
      label="Code Example"
      preview={
        <Box className="blue-monospace">
          <AffirmMessaging
            options={{
              fontColor: '#5B63FF',
              logoColor: 'black',
              fontSize: '16px',
              textAlign: 'center',
            }}
          />
        </Box>
      }
      codeBlock={
        <CodeBlock language="javascript">{`const options = {
  amount: 5000,
  currency: 'USD',
  fontColor: '#5B63FF',
  logoColor: 'black',
  fontSize: '16px',
  textAlign: 'center',
};

const affirmMessageElement = elements.create('affirmMessage', options);`}</CodeBlock>
      }
    />
  </div>
);

export const AffirmMessagingDemo = ({part}: {part: string}) => {
  const {account} = useAccount();
  const [promise, setPromise] = useState(null);

  useEffect(() => {
    setPromise(
      loadStripe(account.keys.publishable, {
        betas: ['affirm_pilot_beta_1'],
      }),
    );
  }, [account.keys.publishable]);

  let partComponent;
  const locale = 'en-US';

  if (part === 'product') {
    partComponent = <ProductDemo />;
  } else if (part === 'logo') {
    partComponent = <LogoDemo />;
  } else if (part === 'css-demo-message') {
    partComponent = <CSSDemoMessage />;
  }

  return (
    <Box className="AffirmMessagingDemo" margin={{top: 12}}>
      <Elements stripe={promise} options={{locale}}>
        {partComponent}
      </Elements>
    </Box>
  );
};
